<template>
  <div class="flex flex-col">
    <div class="input-group" :style="styleObject">
      <div class="input-group-prepend relative">
        <span :style="inputGroupTextDynamicStyle" class="input-group-text">
          <CurrencyDollarIcon
            class="input-icon pointer-events-none absolute left-3 top-1/2 z-[1] h-[16px] !w-[16px] -translate-y-1/2"
          />
        </span>
      </div>
      <input
        v-if="isActive"
        ref="monetoryInputRef"
        v-model="inputPlaceholder"
        :aria-label="t('common.enterAmount')"
        :placeholder="placeholder ? placeholder : t('common.enterAmount')"
        type="text"
        class="form-control form-placeholder"
        :style="{
          ...styleInput,
          borderLeft: 'none !important',
          borderTopLeftRadius: '0 !important',
          borderBottomLeftRadius: '0 !important',
        }"
        :class="classList"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <input
        v-else
        ref="monetoryInputRef"
        :aria-label="t('common.enterAmount')"
        type="text"
        class="form-control"
        :style="{
          ...styleInput,
          borderLeft: 'none !important',
          borderTopLeftRadius: '0 !important',
          borderBottomLeftRadius: '0 !important',
        }"
        :class="classList"
        :placeholder="placeholder"
        :readonly="readonly"
      />
    </div>
    <template v-if="isActive">
      <div
        ref="shortLabelElement"
        :key="resetKey"
        contenteditable="true"
        class="short-label font-inter cursor-text border-0 bg-transparent p-0 text-sm shadow-none focus:outline-none"
        :data-placeholder="t('common.shortLabelPlaceholder')"
        @blur="updateShortLabel"
        @input="handleInputShortLabel"
        @compositionstart="handleCompositionStart"
        @compositionend="compositionEndHandler"
        @keydown="handleSafariCursor"
        @click="(e) => e.stopPropagation()"
        @focus="(e) => e.stopPropagation()"
        v-text="shortLabel"
      ></div>
    </template>
    <template v-else>
      <span v-if="element.shortLabel" class="short-label font-inter break-words text-sm">{{
        element.shortLabel
      }}</span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useIMEComposition } from "@/composition/useIMEComposition";
import { useAppStore } from "@/store/app";
import {
  fixSafariCursor,
  handleShortLabelInput as handleShortLabelInputUtil,
} from "@/util/methods";
import { CurrencyDollarIcon } from "@gohighlevel/ghl-icons/24/outline";
import { onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { handleCompositionStart, handleCompositionEnd, withIMECheck, resetKey } =
  useIMEComposition();
const props = defineProps({
  element: {
    type: Object,
    default: () => {
      return {};
    },
  },
  placeholder: { type: String, default: "" },
  classList: {
    type: Array,
    default: () => {
      return [];
    },
  },
  styleObject: {
    type: Object,
    default: () => {
      return {};
    },
  },
  styleInput: {
    type: Object,
    default: () => {
      return {
        fontSize: "14px",
      };
    },
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:placeholder", "update:shortLabel"]);
const inputPlaceholder = ref(props.placeholder);
const shortLabel = ref(props.element.shortLabel || "");
const shortLabelElement = ref<HTMLElement | null>(null);
const monetoryInputRef = ref<HTMLInputElement | null>(null);
const inputGroupTextDynamicStyle = ref({});
const store = useAppStore();

onMounted(() => {
  syncInputGroupTextBorder();
});

function syncInputGroupTextBorder() {
  if (store.builderConfig?.inputStyleType === "line") {
    const inputStyle = window.getComputedStyle(monetoryInputRef.value as unknown as Element);
    inputGroupTextDynamicStyle.value = {
      border: "none !important",
      borderBottom: inputStyle.borderBottom,
    };
  } else if (monetoryInputRef.value) {
    const inputStyle = window.getComputedStyle(monetoryInputRef.value);
    // Use borderTopRightRadius as reference (it has the actual radius value)
    const borderRadius = inputStyle.borderTopRightRadius;

    inputGroupTextDynamicStyle.value = {
      borderTop: inputStyle.borderTop,
      borderBottom: inputStyle.borderBottom,
      borderLeft: inputStyle.borderTop,
      borderRight: "none !important",
      borderTopLeftRadius: borderRadius,
      borderBottomLeftRadius: borderRadius,
      borderTopRightRadius: "0",
      borderBottomRightRadius: "0",
      background: `#${store?.fieldStyle?.bgColor} !important`,
    };
  }
}

watch(
  () => [props.styleInput, props.isActive],
  () => {
    syncInputGroupTextBorder();
  },
  {
    deep: true,
  }
);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

watch(
  () => props.placeholder,
  (newVal) => {
    inputPlaceholder.value = newVal;
  }
);

watch(
  () => props.element.shortLabel,
  (newVal) => {
    shortLabel.value = newVal || "";
  }
);

const handleInput = () => {
  emit("update:placeholder", inputPlaceholder.value);
};

const handleFocus = () => {
  // Ensure the input has the placeholder value when focused
  if (!inputPlaceholder.value) {
    inputPlaceholder.value = props.placeholder;
  }
};

const handleBlur = () => {
  store.addToHistory();
};

const updateShortLabel = () => emit("update:shortLabel", shortLabel.value);

const rawInputShortLabelHandler = (e: Event) => {
  handleShortLabelInputUtil(e, shortLabel, updateShortLabel);
};

const handleInputShortLabel = withIMECheck(rawInputShortLabelHandler);
const compositionEndHandler = handleCompositionEnd(rawInputShortLabelHandler);
</script>

<style>
.short-label {
  background-color: transparent;
  margin-top: 4px;
  font-size: 14px;
  color: #4b5563;
}

.short-label:empty:before {
  content: attr(data-placeholder);
}

.input-group > .input-group-prepend > .btn,
.input-group > .input-group-prepend > .input-group-text,
.input-group > .input-group-append:not(:last-child) > .btn,
.input-group > .input-group-append:not(:last-child) > .input-group-text,
.input-group > .input-group-append:last-child > .btn:not(:last-child):not(.dropdown-toggle),
.input-group > .input-group-append:last-child > .input-group-text:not(:last-child) {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.input-group-prepend {
  display: flex;
  margin-right: 0px !important;
  border-color: #e5e7eb;
  position: relative;
  border-right: 1px solid #dee5e8;
}

.input-group-text {
  width: 20px !important;
  font-family: Roboto, system, -apple-system, BlinkMacSystemFont, ".SFNSDisplay-Regular",
    "Helvetica Neue", Helvetica, Arial, sans-serif;
  border-right: 1px solid #dee5e8;
  background-color: #ffffff !important;
  border-radius: 0;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  padding: 0.375rem 0.75rem;
  margin-bottom: 0;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057 !important;
  text-align: center;
  white-space: nowrap;
  border: 1px solid #ced4da;
  border-radius: 0.3125rem;
}
</style>
